<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import smoothscroll from 'smoothscroll-polyfill';
import { computed } from 'vue';

import { triggerChapterClicked } from '@/composables/useChapterClickedHook';
import { ARCHIVE_TYPE } from '@/store/constants';
import { globalConfig } from '@/store/globalConfig';
import { requestOpenSharing } from '@/store/streamPostMessenger';
import { useGlobalStyle, useTemplate } from '@/store/templateData';
import { toHoursString } from '@/utils/transformer/time';
import IconMdiShareVariantOutline from '~icons/mdi/share-variant-outline';

defineOptions({
  name: 'ModuleChapterDisplay'
});

smoothscroll.polyfill();

interface Props {
  module?: {
    components?: {
      title?: {
        text?: string;
      };
      subtitle?: {
        text?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      }
    }
  })
});

const components = computed(() => props.module?.components);

const { currentStreamStatus, isReplaying, isModuleIncludingStreamRoom } = useTemplate();

const isChapterArchive = computed(() => {
  return isReplaying.value && currentStreamStatus.value?.archive?.type === ARCHIVE_TYPE.CHAPTER;
});

const chapters = computed(() => {
  if (!isChapterArchive.value) { return []; }

  const archiveData = currentStreamStatus.value?.archive?.data;

  // 類型守衛：確保是 chapter 類型的 data，具有 started_time 和 labels 屬性
  if (!archiveData || Array.isArray(archiveData) || !('started_time' in archiveData) || !('labels' in archiveData)) {
    return [];
  }

  const trimmedStartTime = archiveData.started_time;
  const chaptersLabels = archiveData.labels;
  return chaptersLabels.map((label: { name: string; started_time: number }) => ({
    title: label.name,
    timeString: toHoursString(label.started_time - trimmedStartTime, { precision: 0 }),
    time: label.started_time,
  }));
});

const needRender = computed(() => [
  isChapterArchive.value,
  chapters.value.length > 0,
].every(Boolean));

/**
 * NOTE: scrollbar 下方捲動到 stream room 底部的圖示講解
 *
 *                     ┌─────────────────────────────────┬─┐
 *                  │  │                                 │ │ │
 *                  │  │                                 │ │ │ window.scrollY
 *                  │  │                                 │ │ │
 *                  │  │---------------------------------│█│ ▼
 *                  │  │              │                  │ │
 *                  │  │              │streamRectTop     │ │
 *                  │  │              ▼                  │ │
 *    streamTop ──► │  │  ┌──────────────────────────┐   │ │
 *                  │  │  │ │                        │   │ │
 *                  │  │  │ │                        │   │ │
 *                  │  │  │ │ streamHeight           │   │ │
 *                  │  │  │ │                        │   │ │
 *                  │  │  │ ▼                        │   │ │
 * streamBottom ──► ▼  │  └──────────────────────────┘   │ │
 *                     │                                 │ │
 *                     │---------------------------------│ │
 *                     │                                 │ │
 *                     └─────────────────────────────────┴─┘
 **/

const { height: windowHeight } = useWindowSize();
const scrollToStreamRoomBottom = () => {
  const { localStreamId } = globalConfig.value;
  const streamEl = document.getElementById(localStreamId);
  if (!streamEl) { return; }
  const { height: streamHeight, top: streamRectTop } = streamEl.getBoundingClientRect();
  const streamTop = streamRectTop + window.scrollY;
  const streamBottom = streamTop + streamHeight;
  const scrollTop = streamBottom - windowHeight.value;
  window.scrollTo({ top: scrollTop, behavior: 'smooth' });
};

const onChapterTimeClicked = (seekTime: number) => {
  if (!isModuleIncludingStreamRoom.value) { return; }
  triggerChapterClicked(seekTime);
  scrollToStreamRoomBottom();
};

const triggerStreamRoomSharing = (seconds: number) => {
  scrollToStreamRoomBottom();
  requestOpenSharing(seconds);
};

const { titleStyle, subtitleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-chapter-display" v-if="needRender">
  <div
    class="module-chapter-display__title"
    v-if="components?.title?.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>

  <div
    class="module-chapter-display__subtitle"
    v-if="components?.subtitle?.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>

  <div class="module-chapter-display__chapters" v-if="chapters.length > 0">
    <span
      class="module-chapter-display__chapters__row"
      v-for="chapter in chapters"
      :key="chapter.timeString"
    >
      <button
        class="module-chapter-display__chapters__time"
        type="button"
        @click="onChapterTimeClicked(chapter.time)"
      >
        <time>{{ chapter.timeString }}</time>
      </button>

      <span class="module-chapter-display__chapters__title">{{ chapter.title }}</span>
      <button
        class="module-chapter-display__chapters__share-btn"
        type="button"
        @click="triggerStreamRoomSharing(chapter.time)"
      >
        <IconMdiShareVariantOutline/>
      </button>
    </span>
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-chapter-display {
  @apply px-5 py-4;

  &__title,
  &__subtitle {
    @apply font-medium break-all whitespace-pre-line;

    word-wrap: break-word;
  }

  &__title {
    @apply text-2xl text-center mb-3;
  }

  &__subtitle {
    @apply text-xl text-left mb-8;
  }

  &__chapters {
    @apply flex flex-col;
    @apply mt-4 p-7 bg-gray-100 space-y-2;

    &__title,
    &__time {
      @apply text-sm font-medium mr-2;
    }

    &__title {
      @apply text-black;
    }

    &__time {
      @apply inline-block underline text-primary-light;
      @apply cursor-pointer select-none;
      @apply focus:outline-none;
    }

    &__share-btn {
      @apply inline-flex items-center justify-center align-bottom;
      @apply h-6 w-6 rounded-full bg-white border border-gray-600;
      @apply text-gray-800;
      @apply focus:outline-none;

      svg {
        @apply mr-px -ml-px;

        width: 14px;
        height: 14px;
      }
    }
  }
}

// 平板
.tablet .module-chapter-display {
  @apply px-10;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__chapters {
    &__title,
    &__time,
    &__action {
      @apply text-base;
    }
  }
}

// PC - 1024px
.laptop .module-chapter-display {
  @apply py-8;

  padding-left: 10%;
  padding-right: 10%;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__chapters {
    &__title,
    &__time,
    &__action {
      @apply text-base;
    }
  }
}

// PC - 1440px
.desktop .module-chapter-display {
  @apply px-6 py-8;

  width: $desktop-fixed-width;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__chapters {
    &__title,
    &__time,
    &__action {
      @apply text-base;
    }
  }
}
</style>
